import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { getContrastThemeClass, JabraIcon, resolveBackgroundStyle } from '@gn-audio/jss-components';

import { DualButtonBlock } from 'composables/shared/DualButtonBlock/DualButtonBlock';

import { SliderCardProps } from './sliderTypes';

const SliderCard = ({
  id,
  header,
  description,
  image,
  background,
  icon,
  iconBackground,
  centerText,
  firstBtnText,
  firstBtnAriaLabel,
  firstBtnLink,
  firstBtnIcon,
  firstBtnVariant,
  firstBtnSize,
  secondBtnLink,
  secondBtnVariant,
  secondBtnText,
  secondBtnAriaLabel,
  secondBtnIcon,
  secondBtnSize,
}: SliderCardProps) => {
  const cardThemeClass = getContrastThemeClass(background?.value as string);
  const cardBackgroundStyle = resolveBackgroundStyle(background?.value as string);

  const hasImageOrIcon = !!image?.value?.src || !!icon?.value;

  const renderImageOrIcon = () => {
    if (image?.value?.src) {
      return <Image className="size-full object-cover" field={image} loading="lazy" />;
    }

    if (icon?.value) {
      const iconBackgroundColorStyle = resolveBackgroundStyle(iconBackground?.value as string);
      const iconThemeClass = getContrastThemeClass(iconBackground?.value as string);
      return (
        <div
          style={iconBackgroundColorStyle}
          className="flex size-full items-center justify-center"
        >
          <JabraIcon
            className={classNames(iconThemeClass, 'size-20')}
            Icon={icons[icon.value as keyof typeof icons]}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div
      style={cardBackgroundStyle}
      className={classNames(
        cardThemeClass,
        centerText?.value && 'text-center',
        'flex min-h-64 w-[21rem] flex-col rounded-3xl bg-white shadow-md md:w-[24rem]'
      )}
    >
      {hasImageOrIcon && (
        <div className="relative h-48 w-full overflow-hidden rounded-t-3xl">
          {renderImageOrIcon()}
        </div>
      )}

      <div className="flex grow flex-col px-4 pb-6 pt-4">
        <div>
          {header?.value && (
            <h3 className="heading-5" id={`headerCard-${id}`}>
              {header.value}
            </h3>
          )}

          {description?.value && (
            <p
              className="paragraph"
              dangerouslySetInnerHTML={{ __html: sanitize(description.value) }}
            ></p>
          )}
        </div>

        <DualButtonBlock
          firstBtnText={firstBtnText}
          firstBtnAriaLabel={firstBtnAriaLabel}
          firstBtnLink={firstBtnLink}
          firstBtnIcon={firstBtnIcon}
          firstBtnVariant={firstBtnVariant}
          firstBtnSize={firstBtnSize}
          secondBtnText={secondBtnText}
          secondBtnAriaLabel={secondBtnAriaLabel}
          secondBtnLink={secondBtnLink}
          secondBtnIcon={secondBtnIcon}
          secondBtnVariant={secondBtnVariant}
          secondBtnSize={secondBtnSize}
          className={classNames(
            centerText?.value && 'justify-center',
            'mt-auto flex flex-col flex-wrap gap-2 pt-6 md:flex-row'
          )}
        />
      </div>
    </div>
  );
};

export default SliderCard;
